import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./OnboardingPage.css";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../context/auth";
import InviteCodeModal from "../Modals/inviteCodeModal/InviteCodeModal";
import { addGuestToEvent } from "../helper/addGuestToEvent";
import { addUsherToEvent } from "../helper/addUsherToEvent";

const OnboardingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const mode = location.state?.mode || "";
  const preSelectedRole = location.state?.preSelectedRole || null;
  const eventId = location.state?.eventId || null;
  const eventTitle = location.state?.eventTitle || null;

  const [selectedRole, setSelectedRole] = useState(preSelectedRole || null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [inviteCode, setInviteCode] = useState("");
  const [usherCode, setUsherCode] = useState("");
  const [invite, setInvite] = useState("");
  const [isCodeValid, setIsCodeValid] = useState(true);
  const [hostId, setHostId] = useState("");
  const [isVerifyCodeLoading, setIsVerifyCodeLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (preSelectedRole === "guest" && eventId) {
      setIsModalOpen(true);
    }
  }, [preSelectedRole, eventId]);

  const handleRoleChange = (role) => {
    setSelectedRole(role);
  };

  const handleContinue = () => {
    if (!selectedRole) return;

    if (mode === "login") {
      navigate("/login", { state: { role: selectedRole } });
      return;
    }

    if (selectedRole === "guest" || selectedRole === "usher") {
      setIsModalOpen(true);
    } else {
      navigate("/signup", { state: { role: selectedRole } });
    }
  };

  const handleAccountLinkClick = (e, path) => {
    if (!selectedRole) {
      e.preventDefault();
      return;
    }
  };

  const handleCreateAccountClick = () => {
    if (!selectedRole) return;

    if (selectedRole === "guest" || selectedRole === "usher") {
      setIsModalOpen(true);
      return;
    }

    navigate("/signup", { state: { role: selectedRole } });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setErrorMessage("");

    if (preSelectedRole === "guest" && eventId) {
      navigate(-1);
    }
  };

  const handleProceed = async (code) => {
    if (!code.trim()) {
      setErrorMessage("Please enter an invite code");
      return;
    }

    setIsVerifyCodeLoading(true);
    setErrorMessage("");

    try {
      let hostId;

      const hostLookup = await axios.get(`/invite`, {
        params: { code, role: selectedRole },
      });

      if (!hostLookup.data?.success) {
        setErrorMessage(hostLookup.data?.message || "Invalid invitation code");
        setIsVerifyCodeLoading(false);
        return;
      }

      hostId = hostLookup.data.event.userId;

      const { data: verifyData } = await axios.get(`/invite/${hostId}`, {
        params: {
          code,
          role: selectedRole,
          userId: user?._id || null,
        },
      });

      if (!verifyData?.success && !verifyData?.successStatus) {
        setErrorMessage(verifyData?.message || "Invalid invitation code");
        setIsVerifyCodeLoading(false);
        return;
      }

      if (!user?._id) {
        navigate("/signup", {
          state: {
            role: selectedRole,
            inviteCode: code,
            hostId,
          },
        });
        return;
      }

      let addResponse;

      if (selectedRole === "usher") {
        addResponse = await addUsherToEvent(code, hostId, user._id);
      } else {
        addResponse = await addGuestToEvent(code, hostId, user._id);
      }

      if (addResponse.isEventDatePassed) {
        setErrorMessage("Event date has passed!");
        setIsVerifyCodeLoading(false);
        return;
      }

      if (addResponse.isWrongRoleCode) {
        setErrorMessage("This code is not for your role.");
        setIsVerifyCodeLoading(false);
        return;
      }

      if (addResponse.isAlreadyInvited) {
        setErrorMessage("You are already invited.");
        setIsVerifyCodeLoading(false);
        return;
      }

      navigate("/home/guest", { state: { reloadHome: true } });
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Failed to verify invite";
      setErrorMessage(msg);
    } finally {
      setIsVerifyCodeLoading(false);
    }
  };

  const handleTermsClick = () => {
    navigate("/terms-and-conditions", {
      state: { userType: selectedRole || "host" },
    });
  };

  return (
    <div className="onboarding-container">
      <div className="background-image"></div>

      <main className="main-content">
        <div className="welcome-text">
          <h2>
            Welcome to <span className="highlight">NimbleBash</span> community,
            Let's Party!!!
          </h2>
        </div>

        {preSelectedRole && eventTitle && (
          <div className="event-info-banner">
            <p>
              Joining event: <strong>{eventTitle}</strong>
            </p>
            <p>Please enter your invite code to continue</p>
          </div>
        )}

        <div className="role-selection">
          <div
            className={`role-option ${
              selectedRole === "host" ? "selected" : ""
            }`}
            onClick={() => handleRoleChange("host")}
          >
            <div className="radio-container">
              {selectedRole === "host" && <div className="check-icon"></div>}
              <div className="radio-button"></div>
            </div>
            <div className="profile-icon host-icon"></div>
            <div className="role-info">
              <h3>Host</h3>
              <p>I want to create & Manage NimbleBash Event</p>
            </div>
          </div>

          <div
            className={`role-option ${
              selectedRole === "guest" ? "selected" : ""
            }`}
            onClick={() => handleRoleChange("guest")}
          >
            <div className="radio-container">
              {selectedRole === "guest" && <div className="check-icon"></div>}
              <div className="radio-button"></div>
            </div>
            <div className="profile-icon guest-icon"></div>
            <div className="role-info">
              <h3>Guest</h3>
              <p>I want to join a NimbleBash Event</p>
            </div>
          </div>

          <div
            className={`role-option ${
              selectedRole === "usher" ? "selected" : ""
            }`}
            onClick={() => handleRoleChange("usher")}
          >
            <div className="radio-container">
              {selectedRole === "usher" && <div className="check-icon"></div>}
              <div className="radio-button"></div>
            </div>
            <div className="profile-icon guest-icon"></div>
            <div className="role-info">
              <h3>Usher</h3>
              <p>I want to join a NimbleBash Event as an usher!</p>
            </div>
          </div>
        </div>

        <div className="form-footer">
          <div className="account-section">
            {mode === "signup" ? (
              <p>
                Already have an account?{" "}
                <Link
                  to="/login"
                  state={{ role: selectedRole }}
                  className={`create-account-link ${
                    !selectedRole ? "disabled-link" : ""
                  }`}
                  onClick={(e) => handleAccountLinkClick(e, "/login")}
                  style={{
                    pointerEvents: !selectedRole ? "none" : "auto",
                    opacity: !selectedRole ? 0.5 : 1,
                    cursor: !selectedRole ? "not-allowed" : "pointer",
                  }}
                >
                  Sign In
                </Link>
              </p>
            ) : (
              <p>
                I don't have an Account{" "}
                <button
                  type="button"
                  className={`create-account-link ${
                    !selectedRole ? "disabled-link" : ""
                  }`}
                  onClick={handleCreateAccountClick}
                  disabled={!selectedRole}
                  style={{
                    pointerEvents: !selectedRole ? "none" : "auto",
                    textDecoration: "underline",
                    cursor: !selectedRole ? "not-allowed" : "pointer",
                    opacity: !selectedRole ? 0.5 : 1,
                  }}
                >
                  Create Account
                </button>
              </p>
            )}
          </div>

          <div className="onboarding-button-section">
            <button
              onClick={handleContinue}
              className={`continue-button ${!selectedRole ? "disabled" : ""}`}
              disabled={!selectedRole}
            >
              Continue <span className="arrow">→</span>
            </button>

            <div className="terms-notice">
              Press continue means you agree to our{" "}
              <button
                className="terms-link"
                onClick={handleTermsClick}
                style={{
                  background: "none",
                  border: "none",
                  color: "#006400",
                  textDecoration: "underline",
                  cursor: "pointer",
                  padding: 0,
                  font: "inherit",
                }}
              >
                Terms and Conditions
              </button>
            </div>
          </div>
        </div>
      </main>

      <InviteCodeModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onProceed={handleProceed}
        selectedRole={selectedRole}
        error={errorMessage}
        isLoading={isVerifyCodeLoading}
      />
    </div>
  );
};

export default OnboardingPage;
